import React, { useEffect, useMemo, useRef, useState } from 'react';
// The PURE helper from its own subpath, NOT the `/services` barrel — that barrel drags server-only
// services (AspectsManager → node:async_hooks) into the browser bundle. Same import the task
// transcript uses, for the same reason.
import { sanitizeAgentText } from '@wix/ai-scheduling-assistant-common-lib/agentActionLine';
import { useRouter } from 'next/router';
import { Card, TextButton } from '../ui';
import { AgentDot } from '../../AgentDot';
import { useTranslation } from '../../../lib/i18n';
import { useLocale } from '../../../lib/locale-context';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useMyAgents } from '../../../lib/my-agents';
import { ACTIVITY_PAGE } from '../../../lib/activity-bi';
import {
  ACTIVITY_WINDOW_DAYS,
  ActivityAction,
  ActivityDayBucket,
  ActivityDayGroup,
  groupActivity,
  parseActionDate,
  useActivity,
} from '../../../lib/activity';
import styles from './activity.module.css';

/**
 * The activity log: what the agents DID, newest first, grouped by day.
 *
 * READ THIS BEFORE CALLING IT PARITY — it is a deliberate DIVERGENCE, and web is out in front.
 *
 * The `AgentAction` ledger is written by every sub-agent (`LogAgentActionsTool` describes it to the
 * model as "the user-visible feed of your work") and, before this screen, was rendered on NO surface:
 * web deleted its log in #7560, and both phones then followed — iOS `RootView.swift` routes
 * `.activityLog(agentId: nil)` to `AgentTasksScreen`, Android's `AppRoot.kt` does the same, and both
 * cite web's redirect as the model. `ActivityLogScreen` / `ActivityLog.kt` still exist but are
 * reachable only with a non-nil `agentId`, which nothing passes — so they are effectively dead code.
 *
 * The shared premise was that "its account-wide content is now the flat Tasks list". That does not
 * hold: an `AgentAction` is a record written DURING a task ("Published personal dashboard v4",
 * "757 entities, 83 automations"), and the Tasks list shows the task, never the actions inside it.
 * The ledger was not relocated; it stopped being visible anywhere.
 *
 * So this is a NEW capability on web, not a restoration of parity. The phones are expected to follow
 * by pointing their existing `.activityLog(nil)` destination back at their own log. Until they do the
 * surfaces genuinely differ, which is why this page carries its OWN BI page name instead of borrowing
 * `activity_log` — that name labels the Tasks list on both phones, and one funnel must not join two
 * different screens.
 *
 * ONE ledger. `PulseAction` — what routines did — is deliberately NOT shown: it is the pulse's own
 * audit trail, written as tool input and kept in English by the pulse prompt, with raw plumbing in
 * some rows (a wakeup embeds `(wakeup_id=<guid>)`). Rendering it would put English audit prose, and
 * sometimes a GUID, in front of every non-English user — the same reasoning already written out at
 * `handlers/routines.ts:225-231`, which surfaces only run-START rows for the run-history screen.
 *
 * Divergences from the (dormant) native row, each deliberate:
 *  - a "View task" link; neither phone renders `relatedTaskId` at all.
 *  - a real error state; both native stores swallow the failure and show the empty caption instead.
 *  - `capitalize` touches only the first character, matching Android's `replaceFirstChar`. iOS's
 *    `String.capitalized` title-cases every word; the phones already disagree, so web picks one.
 */
export function ActivityBody({ header }: { header?: React.ReactNode }) {
  const { t } = useTranslation();
  const router = useRouter();
  // The APP locale (the Settings picker), not `router.locale` — this app has no Next i18n routing, so
  // `router.locale` is always undefined and every date would silently format in the BROWSER's locale.
  const { locale } = useLocale();
  const bi = useGenericBi();
  const { state, hasMore, loadingMore, loadMore } = useActivity();
  const teamState = useMyAgents();

  useLoadBi('activity-log-data', { isLoading: state.kind === 'loading', isError: state.kind === 'error' });

  const agentsById = useMemo(() => {
    const agents = teamState.status === 'ready' ? teamState.team.agents : [];
    return new Map(agents.map((a) => [a.id, a]));
  }, [teamState]);

  // `now` is captured per render pass rather than read inside the grouper, so every row in one pass is
  // bucketed against the same instant — a list that straddles midnight cannot show two "Today"s.
  const groups = useMemo(
    () => (state.kind === 'ready' ? groupActivity(state.actions, new Date()) : []),
    [state],
  );

  if (state.kind === 'loading') {
    return (
      <div className={styles.body}>
        {header}
        <LoadingSkeleton />
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className={styles.body}>
        {header}
        <div className={styles.error}>{t('agents.activity.error')}</div>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className={styles.body}>
        {header}
        <Card>
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>{t('agents.activity.empty')}</div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.body}>
      {header}
      <div className={styles.groups}>
        {groups.map((group) => (
          <section key={group.id} className={styles.group}>
            <h2 className={styles.groupTitle}>{sectionTitle(group, t, locale)}</h2>
            <div className={styles.rows}>
              {group.rows.map((action) => (
                <ActivityRow
                  key={action.id}
                  action={action}
                  bucket={group.bucket}
                  agentName={action.agentId ? agentsById.get(action.agentId)?.name : undefined}
                  locale={locale}
                  onOpenTask={
                    action.relatedTaskId
                      ? () => {
                          bi.logPageClickBi({ ...ACTIVITY_PAGE, button_name: 'open_task' }, { componentName: 'open-task' });
                          void router.push(`/tasks/${action.relatedTaskId}`);
                        }
                      : undefined
                  }
                />
              ))}
            </div>
          </section>
        ))}
      </div>
      <Footer hasMore={hasMore} loadingMore={loadingMore} loadMore={loadMore} />
    </div>
  );
}

/** Today / Yesterday / the day itself, in the viewer's locale. */
function sectionTitle(group: ActivityDayGroup, t: (k: string) => string, locale?: string): string {
  if (group.bucket === 'today') return t('agents.activity.today');
  if (group.bucket === 'yesterday') return t('agents.activity.yesterday');
  return group.refDate.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * One action. The header resolves to the agent's real name when the row is attributed, and otherwise
 * falls back to its free-form `label` — the writer omits `agentId` when a sub-agent ran unnamed, and
 * an unattributed row is still work that happened. Same fallback ladder as the native row.
 */
function ActivityRow({
  action,
  bucket,
  agentName,
  locale,
  onOpenTask,
}: {
  action: ActivityAction;
  bucket: ActivityDayBucket;
  agentName?: string;
  locale?: string;
  onOpenTask?: () => void;
}) {
  const { t } = useTranslation();
  const label = (action.label ?? '').trim();
  const heading = agentName || (label ? capitalize(label) : t('agents.detail.fallbackName'));
  // The dot's identity key — the agent when known, else the label, so a given kind of action keeps
  // one colour across the log instead of flickering per row.
  const dotKey = action.agentId || label || action.id;
  const sanitizedAction = sanitizeAgentText(action.action);

  return (
    <Card className={styles.row}>
      <div className={styles.rowHeader}>
        <AgentDot id={dotKey} size={15} />
        <span className={styles.rowName}>{heading}</span>
        <span className={styles.rowTime}>{rowTime(action, bucket, t, locale)}</span>
      </div>
      {/* The agent wrote this line for a person, then sometimes appended a raw id to it
          ("…נשלחה הודעה — outreach_id: a36a10ac-…"). The same sanitizer the task transcript uses
          strips that trailing plumbing; it is shared with both native ports so all three agree. */}
      {sanitizedAction ? (
        <p className={styles.rowAction} dir="auto">
          {sanitizedAction}
        </p>
      ) : null}
      {onOpenTask ? (
        <TextButton className={styles.rowLink} onClick={onOpenTask}>
          {t('agents.activity.openTask')}
        </TextButton>
      ) : null}
    </Card>
  );
}

/** Time for today, "Yesterday, HH:MM" for yesterday, a full date+time for anything older. */
function rowTime(action: ActivityAction, bucket: ActivityDayBucket, t: (k: string) => string, locale?: string): string {
  const date = parseActionDate(action.createdDate);
  if (!date) return '';
  const time = date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  if (bucket === 'today') return time;
  if (bucket === 'yesterday') return `${t('agents.activity.yesterday')}, ${time}`;
  // Weekday included, matching the phones' combined `EEE MMM d jmm` skeleton.
  return date.toLocaleString(locale, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The end-of-window marker, or the sentinel that pulls the next page in. */
function Footer({ hasMore, loadingMore, loadMore }: { hasMore: boolean; loadingMore: boolean; loadMore: () => boolean }) {
  const { t } = useTranslation();
  const sentinel = useRef<HTMLDivElement | null>(null);
  // A page that failed disarms the sentinel for good. Without this the observer re-fires the moment the
  // fetch settles — still intersecting, `hasMore` still true — and a failing page 2 hammers the API
  // forever with nothing on screen to show for it (the error state is first-page-only, by design).
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore || stalled) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !loadMore()) setStalled(true);
      },
      // Prefetch just before the sentinel lands, rather than only once it is literally on screen —
      // the same margin `use-notifications-feed` uses, and the web answer to Android's last-3-items.
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, stalled, loadMore]);

  if (!hasMore || stalled) {
    return <div className={styles.footerNote}>{t('agents.activity.showingLast', { days: ACTIVITY_WINDOW_DAYS })}</div>;
  }
  return (
    <div ref={sentinel} className={styles.footerNote}>
      {loadingMore ? <div className="orion-shimmer" style={{ height: 12, width: 120, margin: '0 auto' }} /> : null}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className={styles.rows}>
      {[70, 55, 62].map((w, i) => (
        <Card key={i} size="sm" className={styles.skelCard}>
          <div className="orion-shimmer" style={{ height: 14, width: `${w}%` }} />
          <div className="orion-shimmer" style={{ height: 10, width: `${w - 20}%`, marginTop: 10 }} />
        </Card>
      ))}
    </div>
  );
}
